/**
 * Strand Discovery Reveal Modal
 *
 * Fires the first time a player acquires a new strand class (via crawl drop
 * or Soulloom merge). A cinematic vertical cascade reveals the strand's
 * portrait, identity, stats, lore paragraph, and gameplay hint.
 *
 * First-time-only: the caller is responsible for checking whether the strand
 * class is already in `discoveredStrands` before invoking `showStrandDiscoveryModal`.
 *
 * Usage:
 *   import { showStrandDiscoveryModal } from '../components/StrandDiscoveryModal';
 *   showStrandDiscoveryModal('emberdancer');
 */

import { StateManager } from '../../managers/StateManager';
import { getFullSpriteUrl, getPortraitUrl } from '../../combat/spriteConfig';
import { formatDefensiveTypes, formatRace } from './GameTerms';
import type { ConfigData, UnitTemplate } from '../../types';

// Mastery tier threshold — "core" identity masteries are cost <= 1.25 (1.00x Core).
// Anything up to 1.25 gets listed in the IDENTITY MASTERY row; above that is splash.
// Identity masteries = Prodigy (tier 1) + Core (tier 2). Anything cheaper than Affinity.
const IDENTITY_MASTERY_MAX_TIER = 2;

/**
 * Get the display list of identity masteries for a strand (Core-tier only).
 * Returns mastery display names joined by ", ". Empty string if none are Core.
 */
function getIdentityMasteries(template: UnitTemplate, configData: ConfigData): string {
  if (!template.masteryCosts) return '';
  const identityIds: string[] = [];
  for (const [id, cost] of Object.entries(template.masteryCosts)) {
    if (typeof cost === 'number' && cost <= IDENTITY_MASTERY_MAX_TIER) {
      identityIds.push(id);
    }
  }
  if (identityIds.length === 0) return '';
  const masteries = (configData as unknown as { masteries?: Record<string, { name: string }> }).masteries ?? {};
  return identityIds.map(id => masteries[id]?.name ?? id).join(', ');
}

/**
 * Build the inner HTML for the modal card, given a strand template and config.
 */
function buildCardHTML(template: UnitTemplate, configData: ConfigData): string {
  // Identity data
  const spriteSrc = getFullSpriteUrl(template.spriteKey) || getPortraitUrl(template.spriteKey);
  const race = formatRace(template.race);
  const defTypesLabel = formatDefensiveTypes(template.defensiveTypes ?? []);
  const metaLine = defTypesLabel ? `${race} \u00b7 ${defTypesLabel}` : race;

  // Pull-quote — optional. Gracefully degrades if absent.
  const pullQuoteHTML = template.pullQuote
    ? `<div class="sd-pullquote">&ldquo;${escapeHtml(template.pullQuote)}&rdquo;</div>`
    : '';

  // Mechanical snapshot
  const defTypes = formatDefensiveTypes(template.defensiveTypes ?? []) || '—';
  const hp = template.hp ?? '?';
  const spd = template.spd ?? '?';
  const focus = template.focus ?? '?';
  const identityMasteryText = getIdentityMasteries(template, configData);
  const identityMasteryRow = identityMasteryText
    ? `
      <div class="sd-mech-row">
        <div class="sd-mech-label">Masteries</div>
        <div class="sd-mech-value">${escapeHtml(identityMasteryText)}</div>
      </div>`
    : `
      <div class="sd-mech-row">
        <div class="sd-mech-label">Identity</div>
        <div class="sd-mech-value">Generalist \u2014 build from drops</div>
      </div>`;

  // Lore paragraph — placeholder if absent.
  const loreText = template.loreParagraph?.trim();
  const loreHTML = loreText
    ? `<div class="sd-lore">${escapeHtml(loreText)}</div>`
    : `<div class="sd-lore sd-lore-placeholder">Your codex entry is still being written.</div>`;

  // Gameplay hint — entire section omitted if both fields are absent.
  const hasLeanInto = template.leanInto && template.leanInto.length > 0;
  const hasPlaysWellWith = template.playsWellWith && template.playsWellWith.length > 0;
  const hintHTML = (hasLeanInto || hasPlaysWellWith)
    ? `
      <div class="sd-hint">
        ${hasLeanInto ? `
          <div class="sd-hint-row">
            <div class="sd-hint-label">Lean Into</div>
            <div class="sd-hint-value">${escapeHtml(template.leanInto!.join(' \u00b7 '))}</div>
          </div>
        ` : ''}
        ${hasPlaysWellWith ? `
          <div class="sd-hint-row">
            <div class="sd-hint-label">Plays Well With</div>
            <div class="sd-hint-value">${escapeHtml(template.playsWellWith!.join(', '))}</div>
          </div>
        ` : ''}
      </div>`
    : '';

  return `
    <div class="sd-kicker">New Strand Discovered</div>
    <div class="sd-hero">
      <div class="sd-portrait">
        ${spriteSrc ? `<img src="${spriteSrc}" alt="${escapeHtml(template.name)}" draggable="false" />` : ''}
      </div>
    </div>
    <div class="sd-identity-text">
      <h2 class="sd-name">${escapeHtml(template.name)}</h2>
      <div class="sd-meta">${escapeHtml(metaLine)}</div>
      ${pullQuoteHTML}
    </div>
    <div class="sd-mech">
      <div class="sd-mech-row">
        <div class="sd-mech-label">Types</div>
        <div class="sd-mech-value">${escapeHtml(defTypes)}</div>
      </div>
      <div class="sd-mech-row">
        <div class="sd-mech-label">Stats</div>
        <div class="sd-mech-value">
          <span class="sd-stat">${hp}<span class="sd-stat-label">HP</span></span>
          <span class="sd-stat">${spd}<span class="sd-stat-label">SPD</span></span>
          <span class="sd-stat">${focus}<span class="sd-stat-label">Focus</span></span>
        </div>
      </div>
      ${identityMasteryRow}
    </div>
    ${loreHTML}
    ${hintHTML}
    <div class="sd-buttons">
      <button type="button" class="sd-btn sd-btn-primary" data-sd-action="continue">Continue</button>
    </div>
  `;
}

function escapeHtml(input: string): string {
  return String(input)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Track a single active modal at a time — stacking discoveries queue instead of overlapping.
let activeOverlay: HTMLElement | null = null;
let activeResolve: (() => void) | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

/**
 * Close the active modal, if any. Resolves the pending promise.
 */
function closeActive(): void {
  if (!activeOverlay) return;
  const overlay = activeOverlay;
  overlay.classList.remove('is-visible');

  // Remove escape listener
  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }

  // Wait for fade-out, then unmount
  window.setTimeout(() => {
    overlay.remove();
    if (activeOverlay === overlay) {
      activeOverlay = null;
      const resolve = activeResolve;
      activeResolve = null;
      if (resolve) resolve();
    }
  }, 220);
}

/**
 * Show the Strand Discovery reveal modal for a given strand class.
 * Returns a promise that resolves when the modal is dismissed.
 *
 * If another discovery modal is already visible, this discovery is queued —
 * the returned promise resolves after all prior modals close and this one is
 * shown and dismissed.
 */
export async function showStrandDiscoveryModal(strandId: string): Promise<void> {
  // If another modal is open, wait for it to close first (serialize the queue).
  while (activeOverlay) {
    await new Promise<void>(res => window.setTimeout(res, 50));
  }

  const configData = StateManager.get('configData') as ConfigData | undefined;
  const template = configData?.unitTemplates?.[strandId];
  if (!template || !configData) {
    // Nothing to show — silently no-op rather than block the user.
    return;
  }

  // Build overlay DOM
  const overlay = document.createElement('div');
  overlay.className = 'strand-discovery-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'sd-modal-title');

  const card = document.createElement('div');
  card.className = 'strand-discovery-card';
  card.innerHTML = buildCardHTML(template, configData);

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  activeOverlay = overlay;
  const promise = new Promise<void>((resolve) => {
    activeResolve = resolve;
  });

  // Click outside closes
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeActive();
  });

  // Card itself stops propagation
  card.addEventListener('click', (e) => e.stopPropagation());

  // Button handlers
  const continueBtn = card.querySelector('[data-sd-action="continue"]');
  continueBtn?.addEventListener('click', () => closeActive());

  // Escape key closes
  escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeActive();
  };
  document.addEventListener('keydown', escHandler);

  // Trigger the cascade on next frame (after the DOM has been painted without .is-visible).
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
    });
  });

  return promise;
}

/**
 * Convenience helper: show the modal ONLY if the given strand class is not
 * already in the player's discoveredStrands. Returns true if the modal was
 * shown, false if the strand was already discovered.
 */
export async function showIfNewDiscovery(
  strandId: string,
  discoveredStrandsSnapshot: string[] | Set<string>,
): Promise<boolean> {
  const already = discoveredStrandsSnapshot instanceof Set
    ? discoveredStrandsSnapshot.has(strandId)
    : discoveredStrandsSnapshot.includes(strandId);
  if (already) return false;
  await showStrandDiscoveryModal(strandId);
  return true;
}

/**
 * Parse the JSON-encoded discoveredStrands string from player state.
 * Returns a Set for O(1) membership checks.
 */
export function parseDiscoveredStrands(playerData: { discoveredStrands?: string } | null | undefined): Set<string> {
  if (!playerData?.discoveredStrands) return new Set();
  try {
    const arr = JSON.parse(playerData.discoveredStrands);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}
